/**
 * Order lifecycle: the state machine, its presentation, and the wire types that
 * the `order_json` database function produces.
 */

import { createTranslator, type MessageKey, type Translator } from './i18n';

export type OrderStatus =
  | 'pending'
  | 'accepted'
  | 'preparing'
  | 'ready'
  | 'completed'
  | 'cancelled';

export const ORDER_STATUSES: OrderStatus[] = [
  'pending',
  'accepted',
  'preparing',
  'ready',
  'completed',
  'cancelled',
];

/** Statuses that still require kitchen attention. */
export const ACTIVE_STATUSES: OrderStatus[] = ['pending', 'accepted', 'preparing', 'ready'];

export type StatusTone = 'neutral' | 'info' | 'progress' | 'success' | 'danger';

export interface OrderLine {
  id: string;
  item_id: string | null;
  name: string;
  /** The option chosen, as it was named when the order was placed. */
  variant_name_snapshot: string | null;
  /** The dish's section, for reading the kitchen's list by category. */
  category_name?: string | null;
  /**
   * The group choices this line was charged for, snapshotted at placement.
   * Absent on orders placed before the groups existed.
   */
  options?: { group: string; name: string; price_cents: number; quantity: number }[];
  unit_price_cents: number;
  quantity: number;
  line_subtotal_cents: number;
  discount_cents: number;
  line_total_cents: number;
  discount_label: string | null;
}

const COOKING: readonly OrderStatus[] = ['accepted', 'preparing'];

/**
 * Whether the kitchen still owes this order's food.
 *
 * The cook page's whole queue: accepted, and not yet made. Exported so the page that
 * lists the tickets and the round engine that batches them cannot disagree about what
 * counts as work — a dish shown on one and missing from the other would be a batch
 * somebody cooks short.
 *
 * @param order The order to judge.
 * @returns True when it belongs on the cook page.
 */
export function isCooking(order: Order): boolean {
  return COOKING.includes(order.status);
}

/** The bucket for a dish whose category is unknown or was never set. */
export const UNCATEGORISED = 'Uncategorised';

/**
 * The acceptance grace a restaurant has until the merchant chooses their own, in
 * seconds.
 *
 * Mirrors `restaurants.accept_grace_seconds`, which is `not null default 300` in
 * the schema and is written from the merchant's settings page (0–3600). What the
 * payload carries is what wins; this is only the fallback for a payload that
 * arrives without it — an older deployed function, or a response cached from
 * before the column existed. Written down here rather than repeated as a bare
 * `300` in the screens, so the two cannot drift apart.
 */
export const DEFAULT_ACCEPT_GRACE_SECONDS = 300;

/**
 * The ready-order chase until a restaurant says otherwise, in seconds.
 *
 * Mirrors `restaurants.ready_chase_seconds`, which is `not null default 600` in
 * the schema and written from the merchant's settings page (0–3600). As with the
 * acceptance grace, what the payload carries wins; this is only the fallback for
 * a payload from before the column existed.
 */
export const DEFAULT_READY_CHASE_SECONDS = 600;

/**
 * How long before a scheduled order's start-by the board warns the kitchen, in
 * seconds.
 *
 * Hardcoded on purpose — ten minutes, and not a setting:
 *
 *   - it is a "notice and start" window for a person, not a cooking time. The
 *     cooking time is already inside `prep_start_at`, so it does not need to
 *     scale with the order: a sixty-minute braise still gets ten minutes'
 *     warning, seventy minutes before pickup;
 *   - the board refetches every ten seconds, so a much shorter window would
 *     arrive after the merchant could have acted on it;
 *   - and it must not be `accept_grace_seconds`, which promises the customer
 *     something different. One knob, one meaning.
 *
 * Ten sits in the 5–15 band that reads as a warning without going amber so
 * early that it stops meaning anything. If a merchant ever asks, it becomes its
 * own setting rather than a second meaning for the grace.
 */
export const START_SOON_LEAD_SECONDS = 600;

/**
 * Whether a scheduled order's start-by is close enough that the kitchen should
 * be told to begin, but not yet past.
 *
 * Disjoint from {@link bookedStartMissed} in spirit: an order whose start-by is
 * merely close is amber, and one that has missed it is red, so the two states
 * never both apply.
 *
 * @param order The order to judge.
 * @param now Epoch milliseconds to compare against; defaults to the clock.
 * @returns True inside the lead window before the start-by.
 */
export function scheduledStartSoon(order: Order, now: number = Date.now()): boolean {
  // Any booked order that is not yet cooking: alerting the kitchen to start is
  // the same job whether or not somebody has accepted it.
  if (order.preparing_at !== null || order.prep_start_at === null) return false;
  const due = Date.parse(order.prep_start_at);
  return now >= due - START_SOON_LEAD_SECONDS * 1000 && now < due;
}

/**
 * Whether a scheduled order was booked inside the kitchen's own prep estimate —
 * already past its start-by the moment it landed.
 *
 * The customer took the checkout's "very soon" warning to place it, which makes
 * it a different thing from an order the kitchen is merely late on, and both
 * sides are told so: the tracking card names the very-soon time rather than
 * implying nobody looked, and the merchant's detail says why it arrived overdue.
 *
 * Read from fields already on the payload rather than recorded at checkout, so
 * an order placed before any of this existed answers correctly too. The one case
 * it reads wrong is a cart changed and placed inside the quote's debounce, where
 * the warning never rendered; recording an acknowledgment is the only thing that
 * would fix that, and it is not worth a field.
 *
 * @param order The order to judge.
 * @returns True when the start-by was already behind it at placement.
 */
export function bookedInsidePrep(order: Order): boolean {
  return (
    order.prep_start_at !== null &&
    Date.parse(order.prep_start_at) < Date.parse(order.placed_at)
  );
}

// ---------------------------------------------------------------------------
// The board's two axes
// ---------------------------------------------------------------------------

/**
 * The status partition the board's tab bar offers.
 *
 * `all` is the union and the other three are disjoint and exhaustive over
 * `ACTIVE_STATUSES`, so the three tab counts always sum to `All`'s. `cooking`
 * holds two statuses on purpose: `merchantActions('pending')` offers only
 * Accept, so an `accepted` order exists, and a tab holding only `preparing`
 * would leave it with no home but `All`.
 */
export type BoardTab = 'all' | 'accept' | 'cooking' | 'ready';

/** The statuses each tab shows, for the three tabs that are not the union. */
export const BOARD_TAB_STATUSES: Record<Exclude<BoardTab, 'all'>, readonly OrderStatus[]> = {
  accept: ['pending'],
  cooking: ['accepted', 'preparing'],
  ready: ['ready'],
};

/**
 * Whether an order belongs to a tab.
 *
 * @param order The order to test.
 * @param tab The tab being viewed.
 * @returns True when the order is a member.
 */
export function inBoardTab(order: Order, tab: BoardTab): boolean {
  if (tab === 'all') return ACTIVE_STATUSES.includes(order.status);
  return BOARD_TAB_STATUSES[tab].includes(order.status);
}

/** How long before its deadline an order reaches the Now band. */
export const NOW_LEAD_SECONDS = START_SOON_LEAD_SECONDS;

/** How far ahead the Next band looks; beyond it an order is on the shelf. */
export const NEXT_LEAD_SECONDS = 3600;

/**
 * How long past its quote a cooking order runs before anyone calls it late.
 *
 * One truth for both sides. The customer's tracking card stops promising and says
 * **"Running late"** five minutes past the estimate, and the board's Late band
 * begins at the same moment — so the counter can look at any late order and know
 * the customer has been told, and neither side can drift from the other.
 *
 * It is a window rather than a tolerance of ours, and it is the only number here
 * that both an alarm and a message read. If a kitchen ever needs padding before
 * the board reacts — acting *ahead* of what the customer has been told — that is a
 * new decision with a new owner, not a quiet second answer to this one.
 */
export const RUNNING_LATE_SECONDS = 300;

/**
 * The instant an unaccepted order's grace is measured from.
 *
 * `placed` — the default, and the schema's — tells the customer early rather than
 * after a long estimate; `estimate` waits until the quote itself. Exported so the
 * board and the customer's card cannot pick different anchors: they read one
 * function, and a missing value falls back the way the column does.
 *
 * @param order The order to judge.
 * @returns An ISO timestamp from the order.
 */
export function acceptGraceAnchor(order: Order): string {
  return order.restaurant_accept_grace_basis === 'estimate'
    ? order.eta_ready_at
    : order.placed_at;
}

/**
 * The moment an order has to be acted on, in epoch milliseconds.
 *
 * One number per order, whatever its kind or status, which is what lets a booked
 * order and a walk-up be sorted against each other. Null only for a state that
 * has no deadline at all.
 *
 * @param order The order to judge.
 * @returns Epoch milliseconds, or null.
 */
export function actBy(order: Order): number | null {
  // Made, and now the customer's move: chase them if nobody comes.
  if (order.status === 'ready') {
    if (!order.ready_at) return null;
    const chase = order.restaurant_ready_chase_seconds ?? DEFAULT_READY_CHASE_SECONDS;
    return Date.parse(order.ready_at) + chase * 1000;
  }
  // Committed to — but a booked order that nobody has begun is still judged by
  // its start-by, however willing the counter was. Accepting is not starting:
  // returning the slot here moved an accepted order's deadline *later* than the
  // pending one it had a moment before, and the board answered by dropping it out
  // of Now and back into Next. `lateAt` never did that, so the two disagreed
  // about one clock; this is the half that was wrong.
  if (order.status === 'accepted' || order.status === 'preparing') {
    const startLate = bookedStartLateAt(order);
    if (startLate !== null && !bookedBeganInTime(order, startLate)) return startLate;
    // Otherwise the promise is the quote, which for a booked order is its slot.
    return Date.parse(order.eta_ready_at);
  }
  if (order.status === 'pending') {
    // An unaccepted order has no promise but the acknowledgement, and *when* the
    // customer stops waiting is the merchant's own setting rather than a number
    // of ours. The board therefore goes late at the moment the customer is told —
    // the same grace, from the same anchor, as the tracking card's warning.
    const grace = order.restaurant_accept_grace_seconds ?? DEFAULT_ACCEPT_GRACE_SECONDS;
    // A booked order is judged by when the kitchen has to start, never by how
    // long ago it was booked: an order placed on Monday for Friday is not late on
    // Monday. The basis does not apply to it for the reason the customer's screen
    // gives — anchoring on `placed_at` would flag a 17:00 order at 09:05.
    if (order.prep_start_at) return Date.parse(order.prep_start_at) + grace * 1000;
    return Date.parse(acceptGraceAnchor(order)) + grace * 1000;
  }
  return null;
}

/**
 * What a booked order's tracking card says under the pickup time.
 *
 * The customer's half of the board's rule, so the two can be checked against
 * each other rather than drifting: it reads {@link lateAt} for the deadline, which
 * is the same number the board's Late band is drawn from.
 *
 * `runningBehind` exists because a booked order can be behind *before* the
 * promise breaks — the kitchen has not begun and cannot make the slot, which can
 * be minutes ahead. It is forward-looking on purpose: a duration there would be
 * negative, and it is the one moment the customer can still decide to wait or
 * call.
 */
export type ScheduledNotice =
  | 'promise'
  | 'notAccepted'
  | 'notAcceptedSoon'
  | 'runningBehind'
  | 'lateBy';

/**
 * Which of those the card should show.
 *
 * @param order The order to judge.
 * @param now Epoch milliseconds to compare against; defaults to the clock.
 * @returns The notice.
 */
export function scheduledNotice(order: Order, now: number = Date.now()): ScheduledNotice {
  // Once it is on the counter the promise under the pickup time still holds —
  // the card's own callout does the talking from there.
  if (order.status === 'ready' || order.status === 'completed' || order.status === 'cancelled') {
    return 'promise';
  }

  const deadline = lateAt(order);
  const past = deadline !== null && deadline <= now;

  if (order.status === 'pending') {
    if (!past) return 'promise';
    // A time the customer knowingly took reads as the second half of the
    // checkout's own warning; one the kitchen is simply late on keeps the plain
    // sentence.
    return bookedInsidePrep(order) ? 'notAcceptedSoon' : 'notAccepted';
  }

  // Past the slot *and* past the board's deadline: the promise has been broken, so
  // it can be counted. Either one alone is only a warning that it will be.
  if (past && now > Date.parse(order.eta_ready_at)) return 'lateBy';
  if (past || now > Date.parse(order.eta_ready_at)) return 'runningBehind';
  return 'promise';
}

/**
 * A chip on a card, as a value the board can filter by.
 *
 * Every one of these is a fact about the order rather than a state, which is what
 * makes it worth narrowing by: "show me the booked ones", "show me what is behind".
 * The status chip is deliberately absent — the tab bar already partitions by status,
 * so tapping one switches tab rather than filtering.
 */
export type OrderChip =
  | 'scheduled'
  | 'bookedInsidePrep'
  | 'pastEstimate'
  | 'waitingTooLong'
  | 'notCollected';

/**
 * Which of those labels are worth filtering by.
 *
 * Explicit rather than derived from the key, because "which problems can be asked
 * for" is a decision: a new pressure label shows on the card and is not offered as
 * a filter until someone adds it here.
 */
const PRESSURE_CHIPS: Partial<Record<string, OrderChip>> = {
  'merchant.orders.pastEstimate': 'pastEstimate',
  'merchant.orders.waitingTooLong': 'waitingTooLong',
  'merchant.orders.notCollected': 'notCollected',
};

/** The dictionary key for a chip's label, for the filter strip. */
export const CHIP_LABEL_KEYS: Record<OrderChip, MessageKey> = {
  scheduled: 'merchant.orders.scheduledBadge',
  bookedInsidePrep: 'merchant.orders.bookedInsidePrepChip',
  pastEstimate: 'merchant.orders.pastEstimate',
  waitingTooLong: 'merchant.orders.waitingTooLong',
  notCollected: 'merchant.orders.notCollected',
};

/**
 * The chips this order carries, in the order a card draws them.
 *
 * One source for two readers: the card draws them and the board filters by them. A
 * chip the card shows but this omits is one a merchant could see and not ask for,
 * which is why the pressure labels are mapped here rather than read off the card.
 *
 * The country badge is the one thing that looks like a chip and is deliberately
 * absent: it is drawn beside the customer as part of the order's identity, in the
 * block that opens the order, so it is never offered as a filter.
 *
 * @param order The order to judge.
 * @param now Epoch milliseconds to compare against; defaults to the clock.
 * @returns The chips, possibly empty.
 */
export function orderChips(order: Order, now: number = Date.now()): OrderChip[] {
  const chips: OrderChip[] = [];
  const pressure = orderPressure(order, now);
  const fromPressure = pressure.labelKey === null ? undefined : PRESSURE_CHIPS[pressure.labelKey];
  if (fromPressure) chips.push(fromPressure);
  if (order.is_scheduled) chips.push('scheduled');
  if (bookedInsidePrep(order)) chips.push('bookedInsidePrep');
  return chips;
}

/** Which band an order falls in, which is the board's ordering. */
export type BoardBand = 'late' | 'now' | 'next' | 'later';

/**
 * How much time pressure an order is under, for the card's chip.
 *
 * `soon` is the lead before a deadline and `overdue` is past it; the two are
 * exclusive, so the chip's colour means one thing at a time. `none` is not a
 * quieter signal — it means the chip has nothing to add that the status beside it
 * has not already said, and the card does not draw it.
 *
 * Here rather than in the card because the board filters by it: a merchant tapping
 * "Past estimate" is asking for the orders this function calls `pastEstimate`, and
 * the two readings have to be one function or they will drift.
 *
 * The pressure is returned for every state, because the card's start-by line, its
 * edge and the board's band all colour from it. The **label is not**: a chip is
 * drawn only where it has something the card does not already say. Five states
 * return `null` for exactly that reason — the four that only repeated the status
 * beside them, and the two scheduling ones, whose lead and deadline the card
 * already prints as `Start by {time}` in the same amber and red.
 *
 * @param order The order to judge.
 * @param now Epoch milliseconds to compare against; defaults to the clock.
 * @returns The pressure, and a dictionary key for a chip when one is owed.
 */
export type OrderPressure = 'none' | 'soon' | 'overdue';

export function orderPressure(
  order: Order,
  now: number = Date.now()
): { labelKey: MessageKey | null; pressure: OrderPressure } {
  // A booked order that is not yet cooking is judged by when the kitchen has to
  // start, not by how long ago it was booked: an order placed on Monday for
  // Friday is not late on Monday, and colouring it red all week says nothing
  // about the cooking. The amber lead before the deadline is the notice a kitchen
  // needs to begin and still make the time — see `START_SOON_LEAD_SECONDS`.
  //
  // This covers an *accepted* order as well as a pending one, because accepting
  // is not starting: the band's Late comes off the same clock, and a chip that
  // went quiet on acceptance would contradict it.
  if (order.status !== 'ready' && order.preparing_at === null && order.prep_start_at) {
    if (bookedStartMissed(order, now)) return { labelKey: null, pressure: 'overdue' };
    if (scheduledStartSoon(order, now)) return { labelKey: null, pressure: 'soon' };
    return { labelKey: null, pressure: 'none' };
  }

  if (order.status === 'pending') {
    // A walk-up: the chip asks the same question the band does, off the same
    // deadline — the merchant's own grace.
    const missed = now > (actBy(order) ?? Number.POSITIVE_INFINITY);
    return {
      labelKey: missed ? 'merchant.orders.waitingTooLong' : null,
      pressure: missed ? 'overdue' : 'none',
    };
  }

  if (order.status === 'ready') {
    const waiting = order.ready_at ? (now - Date.parse(order.ready_at)) / 1000 : 0;
    // The restaurant's own chase window, not a fixed ten minutes: the board and
    // the band both read the same number off the payload.
    const chase = order.restaurant_ready_chase_seconds ?? DEFAULT_READY_CHASE_SECONDS;
    return {
      labelKey: waiting > chase ? 'merchant.orders.notCollected' : null,
      pressure: waiting > chase ? 'overdue' : 'none',
    };
  }

  if (order.status === 'completed' || order.status === 'cancelled') {
    return { labelKey: null, pressure: 'none' };
  }

  // Accepted or preparing: the quote is the promise.
  const overdue = Date.parse(order.eta_ready_at) < now;
  return {
    labelKey: overdue ? 'merchant.orders.pastEstimate' : null,
    pressure: overdue ? 'overdue' : 'none',
  };
}

/**
 * The moment a booked order's start-by obligation is missed, in epoch
 * milliseconds.
 *
 * The start-by is the slot less the estimated prep time, so starting at it
 * finishes at the slot. Missing it by more than the merchant's grace means the
 * slot cannot be made. Null for a walk-up, which has no start-by to miss.
 *
 * @param order The order to judge.
 * @returns Epoch milliseconds, or null.
 */
export function bookedStartLateAt(order: Order): number | null {
  if (!order.prep_start_at) return null;
  const grace = order.restaurant_accept_grace_seconds ?? DEFAULT_ACCEPT_GRACE_SECONDS;
  return Date.parse(order.prep_start_at) + grace * 1000;
}

/**
 * Whether the kitchen began inside the grace the start-by allows.
 *
 * The one expression both {@link actBy} and {@link lateAt} branch on, so the
 * deadline the board sorts by and the moment it calls the order late cannot come
 * apart again — which is what put an accepted order back into Next.
 *
 * @param order The order to judge.
 * @param startLate The moment its start obligation is missed.
 * @returns True when it began in time.
 */
export function bookedBeganInTime(order: Order, startLate: number): boolean {
  return order.preparing_at !== null && Date.parse(order.preparing_at) <= startLate;
}

/**
 * Whether the kitchen missed that start-by: began after it, or has not begun.
 *
 * "Began too late" is a fact about a timestamp, so it does not un-happen when
 * somebody taps Start preparing — which is what keeps the board's Late
 * monotonic.
 *
 * @param order The order to judge.
 * @param now Epoch milliseconds to compare against; defaults to the clock.
 * @returns True when the obligation has been missed.
 */
export function bookedStartMissed(order: Order, now: number = Date.now()): boolean {
  const deadline = bookedStartLateAt(order);
  if (deadline === null) return false;
  return order.preparing_at === null ? now > deadline : Date.parse(order.preparing_at) > deadline;
}

/**
 * The moment an order counts as late.
 *
 * Two paths, because the two kinds of order are promised different things:
 *
 *   * a **booked** order is judged by its start-by ({@link bookedStartLateAt})
 *     until it is actually being cooked — the merchant's grace after the moment
 *     the kitchen had to begin — and by the slot plus that same grace once it
 *     began in time;
 *   * a **walk-up** has no start-by, so it is its quote plus
 *     {@link RUNNING_LATE_SECONDS}, the five minutes the customer is given before
 *     their card stops promising — the one deadline here that both an alarm and a
 *     message read.
 *
 * A ready order is neither: its deadline already carries the restaurant's chase
 * window.
 *
 * This stays apart from {@link actBy} deliberately. `actBy` is the sort key and
 * the Now/Next boundary, and both should mean the deadline itself, not the
 * deadline plus five minutes — otherwise it would quietly shrink what "act within
 * ten minutes" means.
 *
 * @param order The order to judge.
 * @param due Its deadline, already computed.
 * @returns Epoch milliseconds for the late threshold, or null.
 */
export function lateAt(order: Order, due: number | null = actBy(order)): number | null {
  if (due === null) return null;
  const grace = order.restaurant_accept_grace_seconds ?? DEFAULT_ACCEPT_GRACE_SECONDS;
  const startLate = bookedStartLateAt(order);

  // A booked order is judged by its start-by until it is actually being cooked,
  // whatever its status: accepting it is not starting it, and an order that goes
  // Late while nobody has accepted it must not come back out of Late because
  // somebody did. Began in time and it is the slot that is the promise — the slot
  // plus the merchant's own grace, which is the latest a booked order may run.
  if (order.status !== 'ready' && startLate !== null) {
    // `due` is already the start-by plus the grace when it was missed, so only a
    // kitchen that began in time gets the extra window before its slot.
    return bookedBeganInTime(order, startLate) ? due + grace * 1000 : due;
  }

  // A walk-up has no start-by: its quote plus the customer's own five minutes,
  // which is the one deadline here that both an alarm and a message read.
  const cooking = order.status === 'accepted' || order.status === 'preparing';
  return cooking ? due + RUNNING_LATE_SECONDS * 1000 : due;
}

/**
 * The band an order sits in, from its deadline and the clock.
 *
 * Two kinds of order, and the bands mean the same thing on both: what has to be
 * done, ordered by when it has to be done. A **walk-up** is only ever in Now or
 * Late — a customer is waiting for it and there is nothing to schedule, so it is
 * in hand from the moment it lands until its deadline passes, however long its
 * quote, grace or chase window is. A **booked** order is the one with a calendar:
 * it walks Later → Next → Now as its start-by approaches.
 *
 * An order only ever moves toward `late` as the clock runs, never back, because
 * the deadline is fixed at placement and re-based once, on acceptance.
 *
 * @param order The order to place.
 * @param now Epoch milliseconds to compare against; defaults to the clock.
 * @returns The band.
 */
export function boardBand(order: Order, now: number = Date.now()): BoardBand {
  const due = actBy(order);
  // Past its deadline, and only that. There used to be a second, elapsed clock —
  // "accepted and not finished for ten minutes" — carried over from the old Needs
  // attention tab. It was a second answer to the same question, and it answered
  // wrongly twice: it flagged an on-time cook whose quote happened to be long, and
  // a booking accepted today for a slot tomorrow. One clock per path, and each has
  // an owner — the merchant's grace, the customer's five minutes, the restaurant's
  // chase.
  const late = lateAt(order, due);
  if (late !== null && late < now) return 'late';

  // Nobody scheduled it, so it has no later to be in: accept it, cook it, or hand
  // it over. Reading Next here would have the board say "due within the hour" about
  // an order whose own card is offering *Start preparing* — which is what it used to
  // do, for every walk-up whose quote was more than ten minutes out.
  //
  // `ready` counts: the food is made, but the counter is still waiting on the
  // customer, and that obligation expires like any other — at `ready_at + chase`,
  // which is where the Late check above takes it. A shop that sets a long chase
  // therefore holds its ready walk-ups in Now until the window closes, rather than
  // filing them under "due within the hour" for an hour.
  if (ACTIVE_STATUSES.includes(order.status) && !order.prep_start_at) return 'now';

  if (due !== null && due <= now + NOW_LEAD_SECONDS * 1000) return 'now';
  if (due !== null && due <= now + NEXT_LEAD_SECONDS * 1000) return 'next';
  return 'later';
}

/**
 * Earliest deadline first, and among equals the one that has waited longest.
 *
 * Deadline rather than arrival, because the two are not the same promise: a
 * walk-up one minute past its two-minute acknowledgement would otherwise sort
 * above a booked order whose start-by passed six minutes ago.
 *
 * This is the **kitchen's** order, and the cook page uses it directly. The
 * counter's board wants money taken into account too, which is {@link boardUrgency}
 * — kept apart so that the kitchen is never asked a question about payment.
 *
 * @param a Left order.
 * @param b Right order.
 * @returns Negative when `a` comes first.
 */
export function boardSort(a: Order, b: Order): number {
  const dueA = actBy(a) ?? Number.MAX_SAFE_INTEGER;
  const dueB = actBy(b) ?? Number.MAX_SAFE_INTEGER;
  return dueA - dueB || Date.parse(a.placed_at) - Date.parse(b.placed_at);
}

/**
 * The counter's urgency: paid first, then earliest deadline, then longest waiting.
 *
 * A paid order is one a customer has already committed to — the money is in and
 * the food is owed to somebody who is coming. An unpaid one has committed to
 * nothing: it may be abandoned, mistyped, or never collected at all. A counter that
 * works those ahead of a paid ticket spends the kitchen on an order nobody may want.
 *
 * Paid leads the deadline rather than breaking ties with it. Deadlines here are
 * per-second, so a tie essentially never happens and a tiebreak would be invisible;
 * and the point is precisely that a paid ticket due later is worth more attention
 * than an unpaid one due sooner.
 *
 * Bands still come first, because they are applied before this comparator rather
 * than by it: an unpaid Late order stays above a paid Now one. Being overdue and
 * having been paid for are different kinds of urgency, and the board shows them in
 * different sections for that reason.
 *
 * @param a Left order.
 * @param b Right order.
 * @returns Negative when `a` comes first.
 */
export function boardUrgency(a: Order, b: Order): number {
  const paidA = a.paid_at === null ? 1 : 0;
  const paidB = b.paid_at === null ? 1 : 0;
  if (paidA !== paidB) return paidA - paidB;
  return boardSort(a, b);
}

/**
 * How the board's cards are ordered inside a band.
 *
 * `urgency` is the board's own answer — paid first, then earliest deadline — and
 * the other two are the counter's: some kitchens work through the queue in the
 * order it arrived, and would rather see the first order of the rush at the top.
 * Those two are deliberately blind to money: a shop that works oldest-first has
 * said how it wants its queue read, and it is not by payment.
 */
export type BoardOrder = 'urgency' | 'oldest' | 'newest';

/**
 * The comparator for one of those.
 *
 * @param order Which ordering.
 * @returns A comparator for `Array.prototype.sort`.
 */
export function boardComparator(order: BoardOrder): (a: Order, b: Order) => number {
  if (order === 'oldest') return (a, b) => Date.parse(a.placed_at) - Date.parse(b.placed_at);
  if (order === 'newest') return (a, b) => Date.parse(b.placed_at) - Date.parse(a.placed_at);
  return boardUrgency;
}

/**
 * How many orders are in each band, for the headings.
 *
 * @param orders Orders to group, already filtered to a tab.
 * @param now Epoch milliseconds to compare against; defaults to the clock.
 * @returns A count per band.
 */
export function countByBand(orders: Order[], now: number = Date.now()): Record<BoardBand, number> {
  const counts: Record<BoardBand, number> = { late: 0, now: 0, next: 0, later: 0 };
  for (const order of orders) counts[boardBand(order, now)] += 1;
  return counts;
}

/**
 * One option combination inside a batch — "Large", "extra chilli" — as a thing to make.
 *
 * Counted apart rather than summed, because they are not interchangeable: two large and
 * one small is not three of anything makeable in one go.
 */
export interface CookRoundOption {
  name: string;
  quantity: number;
  orderIds: string[];
  /** Of those, the orders the counter accepted after they were already late. */
  lateAcceptedOrderIds: string[];
}

/** One dish to cook inside a round, with the orders it is for. */
export interface CookRoundBatch {
  name: string;
  /** The section the dish sits in, so a round can be read by category. */
  category: string;
  /** The dish's whole batch across its options: what is read from across the stall. */
  quantity: number;
  orderIds: string[];
  lateAcceptedOrderIds: string[];
  /** Empty for a dish sold whole; heaviest first otherwise. */
  options: CookRoundOption[];
}

/**
 * A window of food that can be cooked and handed over together.
 *
 * The unit of batch cooking. Everything in it is wanted at around the same moment,
 * which is what lets one wok serve several orders, and it is named for the moment it
 * must be *done* rather than the moment it started.
 */
export interface CookRound {
  /** Its deadline: the earliest ready-by in it, in epoch milliseconds. */
  readyBy: number;
  /** One window after `readyBy`; an order at or past this opens the next round. */
  until: number;
  batches: CookRoundBatch[];
  orderIds: string[];
  /** The sum of the quantities, which is the number the header counts. */
  itemCount: number;
  /** True when the board's own clock calls anything in this round late. */
  late: boolean;
  /**
   * The earliest moment anything in this round went late, in epoch milliseconds.
   *
   * The worst lateness in it, which is the number the round chips: a round is only as
   * late as the order in it that has waited longest.
   */
  lateBy: number | null;
  /**
   * Orders the counter accepted after the board had already called them late.
   *
   * A booked order keeps the slot it was booked for however late it is accepted, so a
   * counter taking a 12:15 booking at 12:11 — its start-by gone at 12:00 — drops work
   * into a round that may already be in the wok, and the kitchen is never told. This is
   * where it is told. A walk-up can qualify too, if it is accepted after its quote plus
   * the merchant's grace has passed; what it cannot be is late-accepted without being
   * late, because its ready-by is set at acceptance as `now + estimate`.
   */
  lateAcceptedOrderIds: string[];
}

/** The window a round covers when the restaurant has not said otherwise. */
export const DEFAULT_COOK_ROUND_SECONDS = 600;

/**
 * The round window to read a restaurant's queue with.
 *
 * The shop's own setting where it has one, ten minutes where it does not. One
 * function rather than a `??` at each call site, because two screens ask — the
 * cook page and the rail's badge beside it — and a badge that counted the queue
 * in a different window than the page it points at is worse than no badge.
 *
 * @param restaurant The restaurant the queue belongs to, if it is known yet.
 * @returns Seconds one round covers.
 */
export function cookRoundSeconds(
  restaurant: { cook_round_seconds?: number | null } | null | undefined
): number {
  return restaurant?.cook_round_seconds ?? DEFAULT_COOK_ROUND_SECONDS;
}

/**
 * The queue as rounds of work, which is the batch cook's question.
 *
 * Rounds are cut on the **ready-by**, the one timestamp every order carries: a walk-up
 * has no start-by at all, and a booked order's is derived from its slot. Two dishes
 * wanted at the same moment are two dishes that come off the wok together, so that is
 * the axis — and being late stays the board's own clock, so a booked order that should
 * have been started is late here while its slot is still an hour away.
 *
 * Derived from orders that already exist, so it needs no state of its own and nobody
 * has to maintain it. That is also why a round's membership is live:
 * the page flags what joined late rather than pretending the count never moved.
 *
 * @param orders The restaurant's orders.
 * @param now Epoch milliseconds, for the board's lateness clock.
 * @param windowSeconds How long a round runs from its first ready-by.
 * @returns The rounds, earliest deadline first; empty when nothing is left to cook.
 */
export function cookRounds(
  orders: Order[],
  now: number = Date.now(),
  windowSeconds: number = DEFAULT_COOK_ROUND_SECONDS
): CookRound[] {
  // The work itself: the counter has taken it, and it still holds a readable ready-by.
  const work = orders
    .filter(isCooking)
    .map((order) => ({ order, ready: Date.parse(order.eta_ready_at) }))
    .filter((entry) => Number.isFinite(entry.ready))
    .sort((a, b) => a.ready - b.ready || a.order.order_number - b.order.order_number);

  const windowMs = windowSeconds * 1000;
  const cut: { readyBy: number; entries: typeof work }[] = [];
  for (const entry of work) {
    const open = cut[cut.length - 1];
    // A round opens at its earliest ready-by and closes one window later, so it always
    // closes. Chaining without that cap never would: a lunch rush has a ready-by every
    // few minutes, so every order would join the round already open and the service
    // would become one batch — the problem this view exists to solve.
    if (!open || entry.ready >= open.readyBy + windowMs) {
      cut.push({ readyBy: entry.ready, entries: [entry] });
    } else {
      open.entries.push(entry);
    }
  }

  return cut.map((round) => {
    const batches = new Map<
      string,
      {
        name: string;
        category: string;
        quantity: number;
        orderIds: string[];
        lateAcceptedOrderIds: string[];
        options: Map<string, CookRoundOption>;
      }
    >();
    const lateAcceptedOrderIds: string[] = [];
    let itemCount = 0;
    let lateBy: number | null = null;

    for (const { order } of round.entries) {
      const wentLate = lateAt(order);
      if (wentLate !== null && wentLate < now) {
        lateBy = lateBy === null ? wentLate : Math.min(lateBy, wentLate);
      }
      // Accepted after the board had already called this order late. The board's own
      // moment rather than this round's deadline: a booked order goes late at its
      // start-by plus the merchant's grace, which can be long before the round it lands
      // in is due — and that is exactly the acceptance the kitchen is never told about.
      const acceptedLate =
        order.accepted_at !== null && wentLate !== null && Date.parse(order.accepted_at) > wentLate;
      if (acceptedLate) lateAcceptedOrderIds.push(order.id);

      for (const line of order.items) {
        itemCount += line.quantity;
        const category = line.category_name ?? UNCATEGORISED;
        // The combination is the identity, not the portion. A line with no choices
        // contributes to the dish's total and needs no breakdown.
        const option =
          [line.variant_name_snapshot, ...(line.options ?? []).map((o) => o.name)]
            .filter((part): part is string => Boolean(part))
            .join(' · ') || null;

        const key = `${category}\u0000${line.name}`;
        const batch =
          batches.get(key) ??
          {
            name: line.name,
            category,
            quantity: 0,
            orderIds: [],
            lateAcceptedOrderIds: [],
            options: new Map<string, CookRoundOption>(),
          };
        batch.quantity += line.quantity;
        if (!batch.orderIds.includes(order.id)) batch.orderIds.push(order.id);
        if (acceptedLate && !batch.lateAcceptedOrderIds.includes(order.id)) batch.lateAcceptedOrderIds.push(order.id);

        if (option !== null) {
          const counted =
            batch.options.get(option) ?? { name: option, quantity: 0, orderIds: [], lateAcceptedOrderIds: [] };
          counted.quantity += line.quantity;
          if (!counted.orderIds.includes(order.id)) counted.orderIds.push(order.id);
          if (acceptedLate && !counted.lateAcceptedOrderIds.includes(order.id)) counted.lateAcceptedOrderIds.push(order.id);
          batch.options.set(option, counted);
        }

        batches.set(key, batch);
      }
    }

    return {
      readyBy: round.readyBy,
      until: round.readyBy + windowMs,
      batches: [...batches.values()]
        .map((batch) => ({
          name: batch.name,
          category: batch.category,
          quantity: batch.quantity,
          orderIds: batch.orderIds,
          lateAcceptedOrderIds: batch.lateAcceptedOrderIds,
          // The biggest batch is the one worth starting a wok for, so it leads.
          options: [...batch.options.values()].sort(
            (a, b) => b.quantity - a.quantity || a.name.localeCompare(b.name)
          ),
        }))
        .sort((a, b) => b.quantity - a.quantity || a.name.localeCompare(b.name)),
      orderIds: round.entries.map((entry) => entry.order.id),
      itemCount,
      // The board's clock, not the round's: a booked order that should already have
      // been started is late, whatever its slot says.
      late: lateBy !== null,
      lateBy,
      lateAcceptedOrderIds,
    };
  });
}

/**
 * How many batches the kitchen has to cook, which is what the batch view counts.
 *
 * One option combination is one batch — three large and two small is two batches, not
 * one — and a dish due in two rounds is a batch in each, because those are two moments
 * at the wok. Nothing collapses across rounds: that is the whole shape of the view.
 *
 * Here rather than in a screen because two of them show it — the cook page's badge and
 * the number on the rail beside it — and a badge that disagreed with the page it points
 * at is worse than no badge at all.
 *
 * @param orders The restaurant's orders.
 * @param now Epoch milliseconds, for the board's lateness clock.
 * @param windowSeconds How long a round runs from its first ready-by.
 * @returns The number of batches.
 */
export function cookBatchCount(
  orders: Order[],
  now: number = Date.now(),
  windowSeconds: number = DEFAULT_COOK_ROUND_SECONDS
): number {
  return cookRounds(orders, now, windowSeconds).reduce(
    (total, round) =>
      total + round.batches.reduce((count, batch) => count + Math.max(1, batch.options.length), 0),
    0
  );
}

/**
 * The live queue's order: whichever comes first, the slot it is booked for or the
 * moment it landed.
 */
export function compareQueueOrder(a: Order, b: Order): number {
  return Date.parse(a.scheduled_for ?? a.placed_at) - Date.parse(b.scheduled_for ?? b.placed_at);
}

/** Newest first, for the history list. */
export function compareMostRecent(a: Order, b: Order): number {
  return Date.parse(b.placed_at) - Date.parse(a.placed_at);
}

/**
 * A server snapshot with local changes put back on top.
 *
 * The board applies a status change from the response the moment it lands, and
 * realtime separately triggers a refetch. The two race: a snapshot *requested*
 * before a change but *applied* after it still describes the older world, and
 * taking it whole puts the order back on the board — the flicker where a handed-over
 * order returns for a moment and leaves again when the next snapshot catches up.
 * With one order it is invisible; marking a run of them in succession is where it
 * shows.
 *
 * So a snapshot is not applied whole. An order that changed locally since the
 * snapshot was asked for is re-applied on top of it: the snapshot's other content
 * still lands, and the local truth about these orders survives.
 *
 * The caller prunes entries the snapshot already reflects, which is what keeps the
 * list of local changes from growing without bound.
 *
 * @param snapshot The orders the server returned.
 * @param optimistic The orders changed locally since the snapshot was requested.
 * @param compare How the resulting list is ordered, which is the caller's business:
 *   the live queue and the history list order differently.
 * @param keep Whether a locally-changed order belongs in this list at all. The
 *   live queue holds only active orders, so one that was completed *leaves* it —
 *   dropping it is the change. The history list holds every status, so the same
 *   order is put back with its new one.
 * @returns The snapshot with those changes applied.
 */
export function mergeOrders(
  snapshot: Order[],
  optimistic: Order[],
  compare: (a: Order, b: Order) => number,
  keep: (order: Order) => boolean
): Order[] {
  if (optimistic.length === 0) return snapshot;

  let merged = snapshot;
  for (const order of optimistic) {
    merged = merged.filter((existing) => existing.id !== order.id);
    if (keep(order)) merged = [...merged, order];
  }
  return [...merged].sort(compare);
}

/** Whether an order still belongs on the live queue. */
export function isActiveOrder(order: Order): boolean {
  return ACTIVE_STATUSES.includes(order.status);
}

export interface OrderEvent {
  type: string;
  to_status: OrderStatus | null;
  actor: 'system' | 'customer' | 'merchant';
  created_at: string;
}

export interface Order {
  id: string;
  restaurant_id: string;
  restaurant_name: string;
  restaurant_slug: string;
  restaurant_phone: string | null;
  restaurant_address: string | null;
  /**
   * When the platform marked this restaurant, or null.
   *
   * Carried on the order so the tracking card can show the mark beside the shop
   * the customer is walking to without fetching a profile of its own.
   */
  restaurant_verified_since: string | null;
  /** The window before the card says nobody has accepted the order yet. */
  restaurant_accept_grace_seconds: number;
  /** How long this restaurant lets a ready order wait before chasing it. */
  restaurant_ready_chase_seconds: number;
  /** What that window is measured from: the order landing, or the estimate. */
  restaurant_accept_grace_basis: 'placed' | 'estimate';
  timezone: string;
  code: string;
  order_number: number;
  /** HMAC-signed `YMP1` token rendered as a QR for counter collection. */
  pickup_token: string;
  status: OrderStatus;
  customer_name: string;
  customer_phone: string;
  customer_email: string | null;
  note: string | null;
  scheduled_for: string | null;
  is_scheduled: boolean;
  /**
   * When the kitchen has to start so the food is ready at `scheduled_for`: the
   * slot less the estimate captured at placement.
   *
   * Null for an ASAP order, which has no such deadline — it is judged by how
   * long it has waited instead. A forecast rather than a promise, and frozen at
   * placement so the merchant gets one answer rather than one that moves as the
   * queue changes.
   */
  prep_start_at: string | null;
  currency: string;
  subtotal_cents: number;
  discount_cents: number;
  tax_cents: number;
  total_cents: number;
  eta_seconds: number;
  eta_ready_at: string;
  eta_model: string;
  eta_sample_count: number;
  eta_queue_depth: number;
  table_id: string | null;
  /** The table this was ordered from, for delivering it. */
  table_label?: string | null;
  source: string;
  /** The country the order came from, as the platform resolved it. Null if unknown. */
  ordered_from_country: string | null;
  /**
   * True when that country differs from the restaurant's.
   *
   * Computed by the payload rather than here, so every caller cannot disagree
   * about what a mismatch is. Never a refusal — a carrier IP or a VPN puts
   * honest customers elsewhere.
   */
  from_elsewhere: boolean;
  /**
   * How many of this customer's other orders at this restaurant were collected,
   * and how many were no-shows.
   *
   * Counted on read, so they are current the moment the card is drawn. Only
   * no-shows count against a customer: a cancellation the merchant made is the
   * shop's own reason and never follows them.
   */
  collected: number;
  no_shows: number;
  placed_at: string;
  accepted_at: string | null;
  preparing_at: string | null;
  ready_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  /**
   * When the counter took the money, or null while it is unpaid.
   *
   * A fact beside the status rather than a stage of it: a customer waiting in line
   * can pay before the bag is packed, and a regular can settle up next time, so it
   * has two timings and no order. Nothing gates on it and it is reversible, which a
   * status is not.
   */
  paid_at: string | null;
  items: OrderLine[];
  events: OrderEvent[];
}

const STATUS_TONES: Record<OrderStatus, StatusTone> = {
  pending: 'neutral',
  accepted: 'info',
  preparing: 'progress',
  ready: 'success',
  completed: 'success',
  cancelled: 'danger',
};

/** English fallback so callers without a translator still get readable text. */
const fallbackTranslator = createTranslator('en');

/**
 * The short label shown on a status chip.
 *
 * @param status An order status.
 * @param t Translator for the active locale.
 * @returns A localised label.
 */
export function orderStatusLabel(status: OrderStatus, t: Translator = fallbackTranslator): string {
  return t(`orderStatus.${status}` as MessageKey);
}

/**
 * The colour family a status chip should use.
 *
 * @param status An order status.
 * @returns A tone key understood by the UI kit.
 */
export function orderStatusTone(status: OrderStatus): StatusTone {
  return STATUS_TONES[status] ?? 'neutral';
}

/**
 * Whether the database will accept a merchant-driven status change.
 *
 * Mirrors `private.assert_transition`. The UI uses it to avoid offering a
 * button that the server would reject; the server enforces it regardless.
 *
 * @param from Current status.
 * @param to Requested status.
 * @returns True when the transition is legal.
 */
export function canTransition(from: OrderStatus, to: OrderStatus): boolean {
  if (from === to) return true;
  if (from === 'completed' || from === 'cancelled') return false;

  const allowed: Record<OrderStatus, OrderStatus[]> = {
    pending: ['accepted', 'preparing', 'ready', 'cancelled'],
    accepted: ['preparing', 'ready', 'cancelled'],
    preparing: ['ready', 'cancelled'],
    ready: ['completed', 'cancelled'],
    completed: [],
    cancelled: [],
  };

  return allowed[from]?.includes(to) ?? false;
}

export interface MerchantAction {
  to: OrderStatus;
  label: string;
  /** True for the primary forward step, false for the secondary skip/back step. */
  primary: boolean;
}

/**
 * The forward actions a merchant can take on an order.
 *
 * `accepted` offers two: start preparing, or jump straight to ready for a
 * kitchen that does not track the intermediate state. `preparing` and `ready`
 * offer one each.
 *
 * @param status Current status.
 * @returns Ordered actions, primary first; empty for terminal statuses.
 */
export function merchantActions(status: OrderStatus): MerchantAction[] {
  switch (status) {
    case 'pending':
      return [{ to: 'accepted', label: 'Accept', primary: true }];
    case 'accepted':
      return [
        { to: 'preparing', label: 'Start preparing', primary: true },
        { to: 'ready', label: 'Mark ready', primary: false },
      ];
    case 'preparing':
      return [{ to: 'ready', label: 'Mark ready', primary: true }];
    case 'ready':
      return [{ to: 'completed', label: 'Handed over', primary: true }];
    case 'completed':
    case 'cancelled':
      return [];
  }
}

export interface PickupStep {
  key: 'placed' | 'accepted' | 'preparing' | 'ready' | 'completed';
  label: string;
  at: string | null;
  done: boolean;
  current: boolean;
}

/** How far along each status is. `cancelled` is lifted out before this is read. */
const STAGE_RANK: Record<OrderStatus, number> = {
  pending: 0,
  accepted: 1,
  preparing: 2,
  ready: 3,
  completed: 4,
  cancelled: 0,
};

/** The stage each timeline step stands for. */
const STEP_RANK: Record<PickupStep['key'], number> = {
  placed: 0,
  accepted: 1,
  preparing: 2,
  ready: 3,
  completed: 4,
};

/**
 * The customer-facing progress timeline.
 *
 * Accepting and preparing are separate steps. Deciding them by timestamp alone
 * conflated the two: `preparing_at` is null until the kitchen actually starts,
 * and falling back to `accepted_at` ticked "Being prepared" the moment an order
 * was accepted — telling the customer their food was being cooked before anyone
 * had touched it.
 *
 * A step is therefore done once the order has *reached* that stage, which also
 * covers the stages a merchant may skip: `pending` can go straight to
 * `preparing`, and `accepted` straight to `ready`, so a later stage implies the
 * earlier ones. A skipped step shows as done with no timestamp.
 *
 * @param order The order to describe.
 * @returns Ordered steps with completion flags.
 */
export function pickupSteps(order: Order): PickupStep[] {
  const cancelled = order.status === 'cancelled';
  const stage = STAGE_RANK[order.status];
  const reached = (key: PickupStep['key']): boolean => stage >= STEP_RANK[key];

  const steps: PickupStep[] = [
    { key: 'placed', label: 'Order received', at: order.placed_at, done: true, current: false },
    {
      key: 'accepted',
      label: 'Accepted by restaurant',
      at: order.accepted_at,
      done: reached('accepted'),
      current: false,
    },
    {
      key: 'preparing',
      label: 'Being prepared',
      at: order.preparing_at,
      done: reached('preparing'),
      current: false,
    },
    {
      key: 'ready',
      label: 'Ready for pickup',
      at: order.ready_at,
      done: reached('ready'),
      current: false,
    },
    {
      key: 'completed',
      label: 'Collected',
      at: order.completed_at,
      done: reached('completed'),
      current: false,
    },
  ];

  if (cancelled) {
    return [
      steps[0]!,
      { key: 'placed', label: 'Cancelled', at: order.cancelled_at, done: true, current: true },
    ];
  }

  // Mark the last completed step as current, unless the order is fully done.
  const lastDone = [...steps].reverse().find((s) => s.done);
  if (lastDone && order.status !== 'completed') lastDone.current = true;

  return steps;
}

/**
 * Whether an order can still be cancelled by the customer.
 *
 * Matches the guard in `public.cancel_my_order`: only while unacknowledged.
 *
 * @param status Current status.
 * @returns True when self-service cancellation is still permitted.
 */
export function canCustomerCancel(status: OrderStatus): boolean {
  return status === 'pending';
}

/**
 * Total number of individual items in an order.
 *
 * @param order Any object with order lines.
 * @returns The summed quantity.
 */
export function totalItemCount(order: Pick<Order, 'items'>): number {
  return order.items.reduce((sum, line) => sum + line.quantity, 0);
}

/**
 * Counts orders in each active state, for dashboard badges.
 *
 * @param orders Orders to tally.
 * @returns A count per status, always including every key.
 */
export function countByStatus(orders: Order[]): Record<OrderStatus, number> {
  const counts = Object.fromEntries(ORDER_STATUSES.map((s) => [s, 0])) as Record<OrderStatus, number>;
  for (const order of orders) {
    counts[order.status] = (counts[order.status] ?? 0) + 1;
  }
  return counts;
}
